import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import {
  IExercise,
  IUsageTime,
  IStateRegion,
  IMajorFunction,
  IRequest,
  ILocationTemplate,
  IUsageTimeService,
  IRequestContext,
  IRequestLocation,
  IRequestFrequencyGroup,
} from '../types/exercise.type'
import {
  getAllLocations,
  getAllExercises,
  getAllUsageTimes,
  getAllFrequencies,
  getAllStateRegions,
  getAllMajorFunctions,
  getAllRequests,
  getAllLocationTemplates,
  getAllUsageTimeServices,
} from '../services/request-form.service'
import { IRequestEquipment } from '../types/equipment.type'
import { getAllEquipments } from '../services/equipment.service'
import { AuthContext } from './auth.context'
import { parseRequests } from '../utils/functions'

export const RequestContext = createContext<IRequestContext>({
  exercises: [],
  usageTimes: [],
  stateRegions: [],
  handleReset: () => {},
  setExercises: () => {},
  majorFunctions: [],
  exerciseRequests: [],
  usageTimeServices: [],
  locationTemplates: [],
  getRequests: () => {},
  setRequests: () => {},
  selectedRequest: null,
  requestLocations: [],
  requestEquipments: [],
  frequencyGroups: [],
  setSelectedRequest: () => {},
  setRequestLocations: () => {},
  getRequestEquipments: () => {},
  setRequestEquipments: () => {},
  getRequestFrequencies: () => {},
  setFrequencyGroups: () => {},
  selectedRequestLocation: null,
  selectedFrequencyGroup: null,
  setSelectedRequestLocation: () => {},
  setSelectedFrequencyGroup: () => {},
})

export const RequestProvider = ({ children }: { children: ReactNode }) => {
  const [exercises, setExercises] = useState<IExercise[]>([])
  const [usageTimes, setUsageTimes] = useState<IUsageTime[]>([])
  const [stateRegions, setStateRegions] = useState<IStateRegion[]>([])
  const [exerciseRequests, setRequests] = useState<IRequest[]>([])
  const [majorFunctions, setMajorFunctions] = useState<IMajorFunction[]>([])
  const [locationTemplates, setLocationTemplates] = useState<ILocationTemplate[]>([])
  const [usageTimeServices, setUsageTimeServices] = useState<IUsageTimeService[]>([])
  const [selectedRequest, setSelectedRequest] = useState<IRequest | null>(null)
  const [requestEquipments, setRequestEquipments] = useState<IRequestEquipment[]>([])
  const [requestLocations, setRequestLocations] = useState<IRequestLocation[]>([])
  const [frequencyGroups, setFrequencyGroups] = useState<IRequestFrequencyGroup[]>([])
  const [selectedRequestLocation, setSelectedRequestLocation] = useState<IRequestLocation | null>(null)
  const [selectedFrequencyGroup, setSelectedFrequencyGroup] = useState<IRequestFrequencyGroup | null>(null)

  const { user } = useContext(AuthContext)

  const getExercises = async () => {
    const data = await getAllExercises()

    if (data.success && data.data) {
      const parsedExercises = data.data.map((exercise) => ({
        ...exercise,
        start_date: dayjs(exercise.start_date).utc(true),
        end_date: dayjs(exercise.end_date).utc(true),
      }))

      setExercises(parsedExercises)
    }
  }

  const getRequests = async (type: 'my requests' | 'all unit requests', exercise_id: number | null) => {
    if (user) {
      const data = await getAllRequests(
        type === 'my requests' && user.id ? user.id : null,
        exercise_id,
        null, // TODO: filter by tiers server-side via permission scoping
      )

      if (data.success && data.data) {
        const parsedRequests = parseRequests(data.data)

        setRequests(parsedRequests)
      }
    }
  }

  const handleReset = () => {
    setSelectedRequest(null)
    setSelectedRequestLocation(null)
    setSelectedFrequencyGroup(null)
  }

  const getStateRegions = async () => {
    const data = await getAllStateRegions()

    if (data.success && data.data) {
      setStateRegions(data.data)
    }
  }

  const getLocationTemplates = async () => {
    const data = await getAllLocationTemplates()

    if (data.success && data.data) {
      setLocationTemplates(data.data)
    }
  }

  const getUsageTimes = async () => {
    const data = await getAllUsageTimes()

    if (data.success && data.data) {
      setUsageTimes(data.data)
    }
  }

  const getUsageTimeServices = async () => {
    const data = await getAllUsageTimeServices()

    if (data.success && data.data) {
      setUsageTimeServices(data.data)
    }
  }

  const getRequestLocations = async () => {
    if (selectedRequest) {
      const data = await getAllLocations(selectedRequest.id)

      if (data.success && data.data) {
        setRequestLocations(data.data)
      }
    }
  }

  const getMajorFunctions = async () => {
    const data = await getAllMajorFunctions()

    if (data.success && data.data) {
      setMajorFunctions(data.data)
    }
  }

  const getRequestFrequencies = async () => {
    if (selectedRequest) {
      const data = await getAllFrequencies(selectedRequest.id)

      if (data.success && data.data) {
        setFrequencyGroups(data.data)
      }
    }
  }

  const getRequestEquipments = async () => {
    if (selectedRequest) {
      const data = await getAllEquipments(selectedRequest.id)

      if (data.success && data.data) {
        setRequestEquipments(data.data)
      }
    }
  }

  useEffect(() => {
    if (!user) return

    getRequestLocations()
    getRequestEquipments()
    getRequestFrequencies()
  }, [user, selectedRequest])

  useEffect(() => {
    if (!user) return

    getExercises()
    getUsageTimes()
    getStateRegions()
    getMajorFunctions()
    getLocationTemplates()
    getUsageTimeServices()
  }, [user])

  return (
    <RequestContext.Provider
      value={{
        exercises,
        usageTimes,
        stateRegions,
        handleReset,
        setExercises,
        majorFunctions,
        exerciseRequests,
        locationTemplates,
        usageTimeServices,
        getRequests,
        setRequests,
        selectedRequest,
        requestLocations,
        requestEquipments,
        setSelectedRequest,
        frequencyGroups,
        setRequestLocations,
        setRequestEquipments,
        getRequestEquipments,
        setFrequencyGroups,
        getRequestFrequencies,
        selectedRequestLocation,
        selectedFrequencyGroup,
        setSelectedRequestLocation,
        setSelectedFrequencyGroup,
      }}
    >
      {children}
    </RequestContext.Provider>
  )
}
